import React from "react";

import { FieldPath, FieldValues } from "react-hook-form";
import { Typography } from "@mui/material";
import Slider, { SliderProps } from "@mui/material/Slider";

import ControllerInput, { ControllerInputProps } from "../ControllerInput/ControllerInput";

export type SliderInputProps<
    TFieldValues extends FieldValues = FieldValues,
    TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> = Omit<ControllerInputProps<TFieldValues, TName>, "render"> &
    SliderProps & {
        getLongValueText?: (value: number) => string;
    };

const SliderInput = <
    TFieldValues extends FieldValues = FieldValues,
    TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({
    name,
    control,
    defaultValue = null,
    FormControlProps,
    LabelProps,
    hideErrorMessage,
    label,
    fullWidth,
    required = false,
    getLongValueText,
    ...sliderProps
}: SliderInputProps<TFieldValues, TName>): React.ReactElement => {
    return (
        <ControllerInput
            control={control}
            name={name}
            defaultValue={defaultValue}
            label={label}
            LabelProps={LabelProps}
            FormControlProps={FormControlProps}
            required={required}
            fullWidth={fullWidth}
            hideErrorMessage={hideErrorMessage}
            render={({ field: { onChange, value } }) => (
                <>
                    {getLongValueText && (
                        <Typography
                            fontSize="var(--size-14)"
                            color="var(--color-secondary)"
                            fontWeight="bold"
                            textAlign="center"
                        >
                            {getLongValueText(value)}
                        </Typography>
                    )}
                    <Slider
                        name={name}
                        value={value}
                        valueLabelDisplay="auto"
                        onChange={onChange}
                        data-testid={`slider_input-${name}`}
                        {...sliderProps}
                    />
                </>
            )}
        />
    );
};

export default SliderInput;
